import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
} from '@mui/material';
import { useTimezone } from '../../../contexts/TimezoneContext';

interface CustomDateTimeInputProps {
    source: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    validate?: any[];
    defaultValue?: string;
    helperText?: string;
    sx?: any;
    fullWidth?: boolean;
}

export const CustomDateTimeInput: React.FC<CustomDateTimeInputProps> = ({
    source,
    label,
    value,
    onChange,
    validate,
    defaultValue,
    helperText,
    sx,
    fullWidth,
    ...props
}) => {
    // Check if we're in development mode
    const isDevelopment = import.meta.env.DEV;
    
    
    // Initialize default values on first render
    const initializeDefaultValues = () => {
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        const year = tomorrow.getFullYear();
        const month = (tomorrow.getMonth() + 1).toString().padStart(2, '0');
        const day = tomorrow.getDate().toString().padStart(2, '0');
        
        return {
            date: `${year}-${month}-${day}`,
            hour: '00',
            minute: '00'
        };
    };
    
    // Initialize state with default values
    const defaultVals = initializeDefaultValues();
    const [dateValue, setDateValue] = useState(defaultVals.date);
    const [hourValue, setHourValue] = useState(defaultVals.hour);
    const [minuteValue, setMinuteValue] = useState(defaultVals.minute);
    const [hasInitialized, setHasInitialized] = useState(false);
    const { timezone, formatDate } = useTimezone();
    
    // Move getTimezoneOffset after timezone is available
    const timezoneOffsets: { [key: string]: number } = {
        'Asia/Hong_Kong': 8 * 60, // UTC+8
        'Asia/Shanghai': 8 * 60,
        'UTC': 0,
        'Europe/London': -1 * 60,
        'America/New_York': -5 * 60,
    };
    const offsetMinutes = timezoneOffsets[timezone] || 0;
    
    // Set initial value only once on mount
    React.useEffect(() => {
        if (!value && !defaultValue && !hasInitialized) {
            // Use the same logic as updateDateTime for consistency
            const year = parseInt(dateValue.substring(0, 4));
            const month = parseInt(dateValue.substring(5, 7)) - 1;
            const day = parseInt(dateValue.substring(8, 10));
            const hour = parseInt(hourValue);
            const min = parseInt(minuteValue);
            
            let utcDate: Date;
            if (timezone === 'Asia/Hong_Kong' || timezone === 'Asia/Shanghai') {
                utcDate = new Date(Date.UTC(year, month, day, hour - 8, min, 0));
            } else if (timezone === 'UTC') {
                utcDate = new Date(Date.UTC(year, month, day, hour, min, 0));
            } else {
                const offsetHours = offsetMinutes / 60;
                utcDate = new Date(Date.UTC(year, month, day, hour - offsetHours, min, 0));
            }
            
            onChange(utcDate.toISOString());
            setHasInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - run only once on mount
    
    // Handle value changes from parent - but skip on initial mount
    React.useEffect(() => {
        if (value && hasInitialized) {
            // Value is in UTC, convert to system timezone for display
            const utcDate = new Date(value);
            // Use offsetMinutes from above
            const localTime = utcDate.getTime() + (offsetMinutes * 60 * 1000);
            const localDate = new Date(localTime);
            
            const year = localDate.getFullYear();
            const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
            const day = localDate.getDate().toString().padStart(2, '0');
            const hour = localDate.getHours().toString().padStart(2, '0');
            const minute = localDate.getMinutes().toString().padStart(2, '0');
            
            // Only update if values actually changed
            if (`${year}-${month}-${day}` !== dateValue) {
                setDateValue(`${year}-${month}-${day}`);
            }
            if (hour !== hourValue) {
                setHourValue(hour);
            }
            if (minute !== minuteValue) {
                setMinuteValue(minute);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, hasInitialized]);
    
    // Update the form value when date, hour, or minute changes
    const updateDateTime = (newDate: string, newHour: string, newMinute?: string) => {
        if (newDate && newHour) {
            const minute = isDevelopment ? (newMinute || minuteValue || '00') : '00';
            
            // Parse the date components
            const year = parseInt(newDate.substring(0, 4));
            const month = parseInt(newDate.substring(5, 7)) - 1; // Month is 0-indexed
            const day = parseInt(newDate.substring(8, 10));
            const hour = parseInt(newHour);
            const min = parseInt(minute);
            
            // Create a UTC date by treating the input as the configured timezone
            // For Hong Kong (UTC+8), if user selects 00:00, we want UTC to be 8 hours earlier
            let utcDate: Date;
            
            if (timezone === 'Asia/Hong_Kong' || timezone === 'Asia/Shanghai') {
                // Create date in UTC by subtracting the timezone offset
                // Hong Kong 2025-06-25 00:00 = UTC 2025-06-24 16:00
                utcDate = new Date(Date.UTC(year, month, day, hour - 8, min, 0));
            } else if (timezone === 'UTC') {
                // If timezone is UTC, no conversion needed
                utcDate = new Date(Date.UTC(year, month, day, hour, min, 0));
            } else {
                // For other timezones, adjust accordingly
                const offsetHours = offsetMinutes / 60;
                utcDate = new Date(Date.UTC(year, month, day, hour - offsetHours, min, 0));
            }
            
            onChange(utcDate.toISOString());
        } else if (newDate) {
            // For date-only selection, assume 00:00 in the system timezone
            const year = parseInt(newDate.substring(0, 4));
            const month = parseInt(newDate.substring(5, 7)) - 1;
            const day = parseInt(newDate.substring(8, 10));
            
            let utcDate: Date;
            if (timezone === 'Asia/Hong_Kong' || timezone === 'Asia/Shanghai') {
                // Hong Kong midnight = UTC previous day 16:00
                // But handle the case where subtracting 8 hours goes to previous day
                utcDate = new Date(Date.UTC(year, month, day, 0 - 8, 0, 0));
            } else if (timezone === 'UTC') {
                utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0));
            } else {
                const offsetHours = offsetMinutes / 60;
                utcDate = new Date(Date.UTC(year, month, day, 0 - offsetHours, 0, 0));
            }
            
            onChange(utcDate.toISOString());
        } else {
            onChange('');
        }
    };
    
    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = event.target.value;
        setDateValue(newDate);
        updateDateTime(newDate, hourValue, minuteValue);
    };
    
    const handleHourChange = (event: any) => {
        const newHour = event.target.value;
        setHourValue(newHour);
        updateDateTime(dateValue, newHour, minuteValue);
    };
    
    const handleMinuteChange = (event: any) => {
        const newMinute = event.target.value;
        setMinuteValue(newMinute);
        updateDateTime(dateValue, hourValue, newMinute);
    };
    
    // Format display of scheduled time
    const getScheduledTimeDisplay = () => {
        if (!dateValue || !hourValue) return null;
        
        // Get the actual UTC time that will be sent
        const minute = isDevelopment ? (minuteValue || '00') : '00';
        
        // Format the local time display (this is what the user selected)
        const localTimeStr = `${dateValue} ${hourValue}:${minute}`;
        
        // Calculate the UTC time using the same logic as updateDateTime
        const year = parseInt(dateValue.substring(0, 4));
        const month = parseInt(dateValue.substring(5, 7)) - 1;
        const day = parseInt(dateValue.substring(8, 10));
        const hour = parseInt(hourValue);
        const min = parseInt(minute);
        
        let utcDate: Date;
        if (timezone === 'Asia/Hong_Kong' || timezone === 'Asia/Shanghai') {
            utcDate = new Date(Date.UTC(year, month, day, hour - 8, min, 0));
        } else if (timezone === 'UTC') {
            utcDate = new Date(Date.UTC(year, month, day, hour, min, 0));
        } else {
            const offsetHours = offsetMinutes / 60;
            utcDate = new Date(Date.UTC(year, month, day, hour - offsetHours, min, 0));
        }
        
        // Format UTC time
        const utcYear = utcDate.getUTCFullYear();
        const utcMonth = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const utcDay = utcDate.getUTCDate().toString().padStart(2, '0');
        const utcHour = utcDate.getUTCHours().toString().padStart(2, '0');
        const utcMin = utcDate.getUTCMinutes().toString().padStart(2, '0');
        const utcTimeStr = `${utcYear}-${utcMonth}-${utcDay} ${utcHour}:${utcMin} UTC`;
        
        return { 
            localTime: localTimeStr,
            utcTime: utcTimeStr
        };
    };
    
    const timeDisplay = getScheduledTimeDisplay();
    
    return (
        <Box sx={sx}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    {...props}
                    label="Date"
                    type="date"
                    value={dateValue}
                    onChange={handleDateChange}
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Hour</InputLabel>
                    <Select
                        value={hourValue}
                        onChange={handleHourChange}
                        label="Hour"
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#E0E0E0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00C853',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#00C853',
                            },
                            '& .MuiSelect-select': {
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            },
                        }}
                    >
                        {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                            <MenuItem key={hour} value={hour} sx={{ fontFamily: 'monospace' }}>
                                {hour}{isDevelopment ? '' : ':00'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {isDevelopment && (
                    <>
                        <Typography variant="body1" sx={{ mx: 0.5, alignSelf: 'center' }}>:</Typography>
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <InputLabel sx={{ fontSize: '0.875rem' }}>Min</InputLabel>
                            <Select
                                value={minuteValue}
                                onChange={handleMinuteChange}
                                label="Min"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#E0E0E0',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#00C853',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#00C853',
                                    },
                                    '& .MuiSelect-select': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem',
                                    },
                                }}
                            >
                                {/* Show 5-minute intervals in development */}
                                {Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0')).map((minute) => (
                                    <MenuItem key={minute} value={minute} sx={{ fontFamily: 'monospace' }}>
                                        {minute}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
            </Box>
            {timeDisplay && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        Scheduled for: <strong>{timeDisplay.localTime}</strong> ({timezone})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        UTC: {timeDisplay.utcTime}
                    </Typography>
                    {isDevelopment && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                            Development mode: Changes run every 5 minutes
                        </Typography>
                    )}
                </Alert>
            )}
            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};